import { ComplexTabList } from '@/features/ComplexTabList';
import { ContentList } from '@/features/ContentList';
import { CTA } from '@/features/CTA';
import { MarketoForm } from '@/features/MarketoForm';
import {
  CaptureClicksRichText,
  DetailContent,
  EmbeddedContent,
  IconTextGrid,
  Quote,
  SolutionsSummary,
  SquareTileCTA,
} from '@/features/shared';
import { withDataLayerWrapper } from '@/features/shared/HOCs';
import { Video } from '@/features/Video';
import { Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, PropsWithChildren } from 'react';
import { MainContentProps } from './MainContent.interface';
import useStyles from './MainContent.style';

const mapVariantToProps = ({ variant }: any) => {
  if (/^quote$/.test(variant)) {
    return {
      orientation: 'No Image',
    };
  }

  return {};
};

const mapVariantToClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.content;
    }

    if (/^quote$/.test(variant)) {
      return clsx(classes.doubleSpacing);
    }

    if (/^cta$/.test(variant)) {
      return clsx(classes.doubleSpacing);
    }

    return '';
  };

const MainContent = ({
  theme = Theme.light,
  legacyContent,
  detailContent = [],
}: PropsWithChildren<MainContentProps>) => {
  const components = React.useMemo(
    () => ({
      Video: withDataLayerWrapper({ Component: Video, position: 'video' }),
      Quote: withDataLayerWrapper({ Component: Quote, position: 'quote' }),
      CaptureClicksRichText: withDataLayerWrapper({
        Component: CaptureClicksRichText,
        position: 'rich_text',
      }),
      CTA: withDataLayerWrapper({ Component: CTA, position: 'cta' }),
      SquareTileCTA: withDataLayerWrapper({
        Component: SquareTileCTA,
        position: 'square_tile_cta',
      }),
      IconTextGrid: withDataLayerWrapper({
        Component: IconTextGrid,
        position: 'icon_text_grid',
      }),
      SolutionsSummary: withDataLayerWrapper({
        Component: SolutionsSummary,
        position: 'solutions_summary',
      }),
      EmbeddedContent: withDataLayerWrapper({
        Component: EmbeddedContent,
        position: 'embedded_content',
      }),
      ContentList: withDataLayerWrapper({
        Component: ContentList,
        position: 'content_list',
      }),
      ComplexTabList: withDataLayerWrapper({
        Component: ComplexTabList,
        position: 'complex_tab_list',
      }),
      MarketoForm: withDataLayerWrapper({
        Component: MarketoForm,
        position: 'marketo_form',
      }),
    }),
    [],
  );
  const classes = useStyles({});
  const shouldDisplayDetailContent = detailContent && detailContent.length > 0;
  const shouldDisplayLegacy = !shouldDisplayDetailContent && legacyContent;

  return (
    <div className={classes.root}>
      {shouldDisplayLegacy && (
        <CaptureClicksRichText theme={theme} className={classes.content} content={legacyContent} />
      )}
      {shouldDisplayDetailContent && (
        <DetailContent
          content={detailContent}
          mapVariantToProps={mapVariantToProps}
          mapVariantToClassName={mapVariantToClassName(classes)}
          theme={theme}
          components={components}
        />
      )}
    </div>
  );
};

export default memo(MainContent);
